import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import { fmtEur } from "@/lib/format";

const ASSET_COLORS = ["#1E3A8A", "#3B82F6", "#93C5FD"];
const SOURCE_COLORS = ["#0F766E", "#F59E0B", "#F97316"];
const MARGIN_COLORS = ["#3B82F6", "#2563EB", "#10B981", "#059669", "#0F172A"];
const SCEN_COLORS = { base: "#3B82F6", adverse: "#F59E0B", severe: "#EF4444" };

function TooltipBox({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-slate-900 text-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold mb-0.5">{payload[0].payload.name || label}</p>
      <p className="font-mono-fin">{fmtEur(payload[0].value)}</p>
    </div>
  );
}

export function MarginsBar({ ce, t }) {
  const data = [
    { name: t("m_ricavi"), value: ce.valore_produzione },
    { name: "Valore Agg.", value: ce.valore_aggiunto },
    { name: t("m_ebitda"), value: ce.mol_ebitda },
    { name: t("m_ebit"), value: ce.ebit },
    { name: t("m_utile"), value: ce.utile_netto },
  ];
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break">
      <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("chart_margins")}</h3>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false}
              tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={38} />
            <Tooltip content={<TooltipBox />} cursor={{ fill: "#1E293B" }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {data.map((d, i) => <Cell key={d.name} fill={MARGIN_COLORS[i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Donut({ title, data, colors }) {
  const clean = data.filter((d) => d.value && d.value > 0);
  return (
    <div>
      <p className="text-xs font-semibold text-slate-400 text-center mb-1">{title}</p>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={clean} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
              {clean.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip content={<TooltipBox />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function StructureDonuts({ sp, t }) {
  const assets = [
    { name: t("chart_fixed"), value: sp.attivo_fisso },
    { name: t("chart_current_a"), value: sp.attivo_corrente },
  ];
  const sources = [
    { name: t("chart_equity"), value: sp.patrimonio_netto },
    { name: t("chart_consol"), value: sp.passivo_consolidato },
    { name: t("chart_current_l"), value: sp.passivo_corrente },
  ];
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break">
      <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("chart_structure")}</h3>
      <div className="grid grid-cols-2 gap-2">
        <Donut title={t("chart_assets")} data={assets} colors={ASSET_COLORS} />
        <Donut title={t("chart_sources")} data={sources} colors={SOURCE_COLORS} />
      </div>
    </div>
  );
}

const BENCH_METRICS = [
  { cat: "liquidity", key: "current_ratio", unit: "x", higher: true },
  { cat: "leverage", key: "debt_equity", unit: "x", higher: false },
  { cat: "leverage", key: "equity_ratio", unit: "%", higher: true },
  { cat: "leverage", key: "pfn_ebitda", unit: "x", higher: false },
  { cat: "profitability", key: "roe", unit: "%", higher: true },
  { cat: "profitability", key: "ros", unit: "%", higher: true },
  { cat: "profitability", key: "ebitda_margin", unit: "%", higher: true },
  { cat: "efficiency", key: "dso", unit: "gg", higher: false },
];

function _findRatio(ratios, cat, key) {
  const list = ratios?.[cat] || [];
  const item = list.find((r) => r.key === key);
  return item ? { value: item.value, label: item.label } : { value: null, label: key };
}

function _verdict(co, se, higher, t) {
  if (co === null || co === undefined || se === null || se === undefined || se === 0) {
    return { label: "—", tone: "na", color: "#94A3B8" };
  }
  const rel = (co - se) / Math.abs(se);
  const better = higher ? rel >= 0 : rel <= 0;
  if (Math.abs(rel) <= 0.08) return { label: t("bench_inline"), tone: "inline", color: "#94A3B8" };
  if (better) return { label: t("bench_above"), tone: "above", color: "#10B981" };
  return { label: t("bench_below"), tone: "below", color: "#EF4444" };
}

export function BenchmarkPanel({ ratios, benchmark, rating, lang, t }) {
  if (!benchmark) return null;
  const label = lang === "it" ? benchmark.label_it : benchmark.label_en;
  const rows = BENCH_METRICS.map((m) => {
    const { value: co, label: lbl } = _findRatio(ratios, m.cat, m.key);
    const se = benchmark.ratios?.[m.key] ?? null;
    const max = Math.max(Math.abs(co || 0), Math.abs(se || 0), 0.0001);
    return { ...m, label: lbl, co, se, coPct: ((co || 0) / max) * 100, sePct: ((se || 0) / max) * 100, v: _verdict(co, se, m.higher, t) };
  });
  const scoreDelta = (rating?.score ?? 0) - (benchmark.avg_score ?? 0);
  const dRound = Math.round(scoreDelta);
  const claim = (() => {
    const it = lang === "it";
    if (Math.abs(dRound) <= 2) {
      return { text: it ? `Il rating è allineato alla media del settore ${label}.` : `The rating is in line with the ${label} sector average.`, color: "#94A3B8", bg: "#94A3B814" };
    }
    if (dRound > 0) {
      return { text: it ? `Il rating supera di ${dRound} punti la media del settore ${label}.` : `The rating is ${dRound} points above the ${label} sector average.`, color: "#10B981", bg: "#10B98114" };
    }
    return { text: it ? `Il rating è ${Math.abs(dRound)} punti sotto la media del settore ${label}.` : `The rating is ${Math.abs(dRound)} points below the ${label} sector average.`, color: "#EF4444", bg: "#EF444414" };
  })();

  const fmtV = (v, unit) => (v === null || v === undefined ? "n/d" : `${Number(v).toLocaleString(lang === "it" ? "it-IT" : "en-US", { maximumFractionDigits: unit === "gg" ? 0 : 2 })}${unit === "x" ? "×" : unit === "%" ? "%" : unit === "gg" ? " gg" : ""}`);

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 print-break" data-testid="benchmark-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div>
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{t("bench_ateco")}: {benchmark.ateco}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">{t("bench_score_vs")}</p>
          <p className="font-mono-fin text-lg font-semibold" style={{ color: scoreDelta >= 0 ? "#10B981" : "#EF4444" }} data-testid="benchmark-score-delta">
            {rating?.score} <span className="text-slate-500 text-sm">/ {benchmark.avg_score}</span>
            <span className="ml-1 text-xs">({scoreDelta >= 0 ? "+" : ""}{Math.round(scoreDelta)})</span>
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-lg px-3 py-2.5 text-sm font-medium" style={{ color: claim.color, backgroundColor: claim.bg }} data-testid="benchmark-claim">
        {claim.text}
      </div>

      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-12 items-center gap-2">
            <div className="col-span-4 sm:col-span-3">
              <p className="text-xs font-medium text-slate-200 truncate">{r.label}</p>
            </div>
            <div className="col-span-5 sm:col-span-6 space-y-1">
              <div className="flex items-center gap-2">
                <div className="h-2.5 rounded-full" style={{ width: `${Math.min(100, r.coPct)}%`, minWidth: 4, backgroundColor: r.v.color }} />
                <span className="font-mono-fin text-[11px] text-slate-200 whitespace-nowrap">{fmtV(r.co, r.unit)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 rounded-full bg-slate-700" style={{ width: `${Math.min(100, r.sePct)}%`, minWidth: 4 }} />
                <span className="font-mono-fin text-[11px] text-slate-500 whitespace-nowrap">{fmtV(r.se, r.unit)}</span>
              </div>
            </div>
            <div className="col-span-3 text-right">
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: r.v.color, backgroundColor: `${r.v.color}1A` }}>{r.v.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-full bg-slate-700 inline-block" /> {t("bench_company")}</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-full bg-slate-700 inline-block" /> {t("bench_sector")}</span>
        <span className="ml-auto italic">{t("bench_indicative")}</span>
      </div>
    </div>
  );
}

export function ScenarioScoreChart({ scenarios, lang, t }) {
  const data = scenarios.map((s) => ({
    name: lang === "it" ? s.label_it : s.label_en,
    key: s.key,
    value: s.metrics.score,
  }));
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break">
      <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("chart_scenario_score")}</h3>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={70} />
            <Tooltip cursor={{ fill: "#1E293B" }} formatter={(v) => [`${v}/100`, "Score"]} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
              {data.map((d) => <Cell key={d.key} fill={SCEN_COLORS[d.key] || "#3B82F6"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


function TrendTooltip({ active, payload, label, isPct }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-slate-900 text-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="font-mono-fin flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: {isPct ? `${Number(p.value).toLocaleString()} ` : fmtEur(p.value)}
        </p>
      ))}
    </div>
  );
}

export function TrendCharts({ trend, t }) {
  if (!Array.isArray(trend) || trend.length < 2) return null;
  const kfmt = (v) => `${Math.round((v || 0) / 1000)}k`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="trend-charts">
      {/* Revenue / EBITDA / Net income */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break lg:col-span-2">
        <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("trend_revenue")}</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={trend} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E293B" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={kfmt} width={40} />
              <Tooltip content={<TrendTooltip />} cursor={{ fill: "#1E293B" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="ricavi" name="Ricavi" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="mol_ebitda" name="EBITDA" fill="#22D3EE" radius={[4, 4, 0, 0]} />
              <Bar dataKey="utile_netto" name="Utile" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Synthetic score by year */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break">
        <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("trend_score")}</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E293B" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30} />
              <Tooltip formatter={(v, n, p) => [`${v}/100 (${p.payload.grade})`, "Score"]} />
              <Line type="monotone" dataKey="score" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4, fill: "#1E3A8A" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Equity & PFN */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break lg:col-span-3">
        <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("trend_equity")}</h3>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E293B" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={kfmt} width={44} />
              <Tooltip content={<TrendTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Line type="monotone" dataKey="patrimonio_netto" name="Patrimonio netto" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="pfn" name="PFN" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key ratios by year */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 print-break lg:col-span-3 overflow-x-auto" data-testid="trend-indices">
        <h3 className="font-heading text-sm font-semibold text-white mb-4">{t("trend_indices")}</h3>
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="text-left text-slate-400">
              <th className="py-2 pr-4 font-medium">{t("year_label")}</th>
              <th className="py-2 px-3 font-medium text-right">DSO (gg)</th>
              <th className="py-2 px-3 font-medium text-right">PFN / EBITDA (×)</th>
              <th className="py-2 px-3 font-medium text-right">Interest Coverage (×)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {trend.map((r) => (
              <tr key={r.year}>
                <td className="py-2 pr-4 font-semibold text-slate-100">{r.year}</td>
                <td className="py-2 px-3 text-right font-mono-fin text-slate-200">{r.dso == null ? "n/d" : Math.round(r.dso)}</td>
                <td className="py-2 px-3 text-right font-mono-fin text-slate-200">{r.pfn_ebitda == null ? "n/d" : Number(r.pfn_ebitda).toFixed(2)}</td>
                <td className="py-2 px-3 text-right font-mono-fin text-slate-200">{r.coverage == null ? "n/d" : Number(r.coverage).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
